/**
 * Operations › Couriers on jobs (W4-COURIER-RELEASE, D378, ADR-0051) — framework-neutral view-models for the Admin
 * support screen where an operator (the SERVICE_AGENT atendente at its city, or OPS/admins) finds a courier stuck on a
 * courier job and releases it with a reason code.
 *
 *   - which jobs are STUCK (a locked handover PIN, or a courier whose last step is older than a named threshold) and
 *     the order the list shows them in (stuck first, then the server's newest-first order);
 *   - the reason vocabulary the release dialog offers (`PIN_LOCKED` only on a locked handover) and the draft rule the
 *     server enforces (a reason is required; `OPERATIONAL_OTHER` needs a note; a note is at most 500 characters);
 *   - the message ids for a step, a phase (what the release will do), an outcome and a refusal.
 *
 * The console only FILTERS and explains: the server authorizes `courier_job:view` / `courier_job:release` at the job's
 * market and decides what a release does. Before the pickup the job goes back to dispatch and is offered to ANOTHER
 * courier; after it the job ends not delivered. Either way the courier is freed.
 */
import {
  COURIER_RELEASE_ATTRIBUTIONS,
  COURIER_RELEASE_NOTE_MAX_LENGTH,
  COURIER_RELEASE_REASONS,
  afterPickupCauseOfReason,
  type CourierReleaseAttribution,
  type AfterPickupMoney,
  type ApiError,
  type SendAfterPickupOutcomeView,
  type SendAfterPickupPreview,
  type CourierDispatchJob,
  type CourierReleaseReason,
  type OperatingMarketView,
} from '@vello/api-client';
import { grantsAllow, type KnownGrants } from './console-access.js';
import { countryOfMarket, marketsOfRegion } from './scope.js';
import { type ScopeState } from './shell.js';

/** A courier holding a job whose last step is at least this old (seconds) is shown as stuck: 15 minutes. */
export const COURIER_STUCK_AFTER_SECONDS = 15 * 60;

/** How often the screen re-reads the list while the tab is visible (milliseconds). */
export const COURIER_JOBS_POLL_MS = 15_000;

/** The engagement states the console names (anything else falls back to the dispatch status). */
const ENGAGEMENT_STATES: readonly string[] = ['en_route_pickup', 'at_pickup', 'en_route_drop', 'at_drop', 'completed'];
/** The dispatch statuses the console names. */
const DISPATCH_STATUSES: readonly string[] = ['SEARCHING', 'ASSISTED', 'ASSIGNED', 'CANCELLED', 'FAILED'];
const OUTCOMES: readonly string[] = ['REDISPATCHED', 'NOT_DELIVERED', 'SOURCE_CANCELLED'];

/** Whether a courier holds the job right now (assigned, not yet delivered). */
export function courierHoldsJob(job: CourierDispatchJob): boolean {
  return job.dispatchStatus === 'ASSIGNED' && job.courierId !== null && job.engagementState !== null && job.engagementState !== 'completed';
}

export type CourierJobStuck =
  | { readonly kind: 'pin_locked' }
  /** D856: the pickup code is locked (too many wrong codes at the pickup). */
  | { readonly kind: 'pickup_locked' }
  | { readonly kind: 'idle'; readonly minutes: number }
  | undefined;

/**
 * Why a job needs the operator, or `undefined`. Only a job a courier still HOLDS can be stuck: a released or ended
 * job keeps its `pinLocked` flag as history, and flagging it again would send the operator to a job with nobody on it.
 */
export function courierJobStuck(job: CourierDispatchJob, thresholdSeconds: number = COURIER_STUCK_AFTER_SECONDS): CourierJobStuck {
  if (!courierHoldsJob(job)) return undefined;
  if (job.pinLocked) return { kind: 'pin_locked' };
  if (job.pickupLocked === true) return { kind: 'pickup_locked' };
  const age = job.activeStepAgeSeconds;
  if (age !== null && age >= thresholdSeconds) return { kind: 'idle', minutes: Math.floor(age / 60) };
  return undefined;
}

/** Whole minutes since the courier's last step, while it holds the job; `undefined` otherwise. */
export function courierStepMinutes(job: CourierDispatchJob): number | undefined {
  return job.activeStepAgeSeconds === null ? undefined : Math.floor(Math.max(0, job.activeStepAgeSeconds) / 60);
}

/**
 * The list order: stuck jobs first — a locked PIN before an idle courier, and among idle couriers the longest stalled
 * first — then every other job in the server's order (newest first). Stable; never mutates the input.
 */
export function sortCourierJobs(jobs: readonly CourierDispatchJob[], thresholdSeconds: number = COURIER_STUCK_AFTER_SECONDS): readonly CourierDispatchJob[] {
  const rank = (job: CourierDispatchJob): number => {
    const stuck = courierJobStuck(job, thresholdSeconds);
    return stuck === undefined ? 2 : stuck.kind === 'pin_locked' || stuck.kind === 'pickup_locked' ? 0 : 1;
  };
  return jobs
    .map((job, index) => ({ job, index, rank: rank(job), age: job.activeStepAgeSeconds ?? 0 }))
    .sort((a, b) => a.rank - b.rank || (a.rank === 1 ? b.age - a.age : 0) || a.index - b.index)
    .map((entry) => entry.job);
}

/** How many jobs of the list are stuck (the screen's attention line). */
export function stuckCourierJobCount(jobs: readonly CourierDispatchJob[], thresholdSeconds: number = COURIER_STUCK_AFTER_SECONDS): number {
  return jobs.filter((job) => courierJobStuck(job, thresholdSeconds) !== undefined).length;
}

/** A job whose handover or (D856) pickup is locked by too many wrong codes. */
type CodeLockState = Pick<CourierDispatchJob, 'pinLocked'> & { readonly pickupLocked?: boolean };
const codeLocked = (job: CodeLockState): boolean => job.pinLocked || job.pickupLocked === true;

/** The reasons the dialog offers for a job: `PIN_LOCKED` only when the handover or the pickup is locked (the server refuses it otherwise). */
export function releaseReasonsFor(job: CodeLockState): readonly CourierReleaseReason[] {
  return COURIER_RELEASE_REASONS.filter((reason) => reason !== 'PIN_LOCKED' || codeLocked(job));
}

/** The reason the dialog opens with: `PIN_LOCKED` on a locked handover or pickup, otherwise none (the operator must choose). */
export function defaultReleaseReason(job: CodeLockState): CourierReleaseReason | '' {
  return codeLocked(job) ? 'PIN_LOCKED' : '';
}

/** Whether a reason needs a note (the server answers `COURIER_RELEASE_NOTE_REQUIRED` without one). */
export function releaseNoteRequired(reasonCode: string): boolean {
  return reasonCode === 'OPERATIONAL_OTHER';
}

/** D858 (owner 2026-10-31): a PIN-locked release must say whose error it was (the server answers `COURIER_RELEASE_ATTRIBUTION_REQUIRED` without it). */
export function releaseAttributionRequired(reasonCode: string): boolean {
  return reasonCode === 'PIN_LOCKED';
}

export interface CourierReleaseDraft {
  readonly reasonCode: string;
  readonly note: string;
  /** D858: whose error a PIN-locked release was (`RECIPIENT` / `COURIER`); empty until chosen. Ignored for any other reason. */
  readonly attribution?: string;
}

export type CourierReleaseDraftIssue = 'REASON_REQUIRED' | 'REASON_INVALID' | 'ATTRIBUTION_REQUIRED' | 'NOTE_REQUIRED' | 'NOTE_TOO_LONG';

export type CourierReleaseDraftResult =
  | { readonly ok: true; readonly reasonCode: CourierReleaseReason; readonly note?: string; readonly attribution?: CourierReleaseAttribution }
  | { readonly ok: false; readonly issue: CourierReleaseDraftIssue };

/**
 * The client mirror of the server's draft rule: a reason from the vocabulary (and applicable to the job when one is
 * given — `PIN_LOCKED` needs a locked handover and, D858, an attribution: whose error it was), a note of at most 500 characters, required for `OPERATIONAL_OTHER`.
 * The note is trimmed; an empty note is omitted from the request; the attribution travels only with `PIN_LOCKED`.
 */
export function validateReleaseDraft(draft: CourierReleaseDraft, job?: CodeLockState): CourierReleaseDraftResult {
  const reasonCode = draft.reasonCode.trim();
  if (reasonCode === '') return { ok: false, issue: 'REASON_REQUIRED' };
  const allowed: readonly string[] = job === undefined ? COURIER_RELEASE_REASONS : releaseReasonsFor(job);
  if (!allowed.includes(reasonCode)) return { ok: false, issue: 'REASON_INVALID' };
  const attribution = (COURIER_RELEASE_ATTRIBUTIONS as readonly string[]).includes(draft.attribution ?? '') ? (draft.attribution as CourierReleaseAttribution) : undefined;
  if (releaseAttributionRequired(reasonCode) && attribution === undefined) return { ok: false, issue: 'ATTRIBUTION_REQUIRED' };
  const note = draft.note.trim();
  if (note.length > COURIER_RELEASE_NOTE_MAX_LENGTH) return { ok: false, issue: 'NOTE_TOO_LONG' };
  if (releaseNoteRequired(reasonCode) && note === '') return { ok: false, issue: 'NOTE_REQUIRED' };
  return { ok: true, reasonCode: reasonCode as CourierReleaseReason, ...(note !== '' ? { note } : {}), ...(releaseAttributionRequired(reasonCode) && attribution !== undefined ? { attribution } : {}) };
}

/** The kinds of job the list can be narrowed to: a Send parcel or a food order (the server's `kind`). */
export const COURIER_JOB_KINDS: readonly CourierDispatchJob['kind'][] = ['send', 'food'];

/**
 * The stages the list can be narrowed to, in the order a job lives them: waiting for a courier, the four steps a courier
 * walks, and the three ways it ends. Each is `{ dispatchStatus, engagementState }` so the screen names it with the same
 * catalog vocabularies as the row (`courier.dispatch_status` / `courier.engagement_state`).
 */
export const COURIER_JOB_STAGES: readonly { readonly key: string; readonly dispatchStatus: string; readonly engagementState: string | null }[] = [
  { key: 'SEARCHING', dispatchStatus: 'SEARCHING', engagementState: null },
  { key: 'ASSISTED', dispatchStatus: 'ASSISTED', engagementState: null },
  ...['en_route_pickup', 'at_pickup', 'en_route_drop', 'at_drop', 'completed'].map((s) => ({ key: s, dispatchStatus: 'ASSIGNED', engagementState: s })),
  { key: 'CANCELLED', dispatchStatus: 'CANCELLED', engagementState: null },
  { key: 'FAILED', dispatchStatus: 'FAILED', engagementState: null },
];

/** The stage key of a job: the courier's step while assigned (and named), else the dispatch status. */
export function courierJobStageKey(job: Pick<CourierDispatchJob, 'dispatchStatus' | 'engagementState'>): string {
  if (job.dispatchStatus === 'ASSIGNED' && job.engagementState !== null && ENGAGEMENT_STATES.includes(job.engagementState)) return job.engagementState;
  return job.dispatchStatus;
}

/** The jobs matching the screen's filters; an empty `kind` / `stage` means "all". Order is kept. */
export function filterCourierJobs(jobs: readonly CourierDispatchJob[], filter: { readonly kind: string; readonly stage: string }): readonly CourierDispatchJob[] {
  return jobs.filter((job) => (filter.kind === '' || job.kind === filter.kind) && (filter.stage === '' || courierJobStageKey(job) === filter.stage));
}

/** The message id naming where the job is: the courier's step while one holds it, else the dispatch status. */
export function courierStepMessageId(job: Pick<CourierDispatchJob, 'dispatchStatus' | 'engagementState'>): string {
  if (job.dispatchStatus === 'ASSIGNED' && job.engagementState !== null && ENGAGEMENT_STATES.includes(job.engagementState)) return `cjobs.step.${job.engagementState}`;
  if (DISPATCH_STATUSES.includes(job.dispatchStatus)) return `cjobs.status.${job.dispatchStatus}`;
  return 'cjobs.status.unknown';
}

/** What a release will do, by phase (before the pickup → another courier; after → not delivered). */
export function releasePhaseMessageId(phase: CourierDispatchJob['release']['phase']): string {
  return phase === 'before_pickup' ? 'cjobs.release.phase.before_pickup' : phase === 'after_pickup' ? 'cjobs.release.phase.after_pickup' : 'cjobs.release.phase.unknown';
}

/** A release outcome, as the short label the list's "last release" column shows. */
export function releaseOutcomeMessageId(outcome: string): string {
  return OUTCOMES.includes(outcome) ? `cjobs.outcome.${outcome}` : 'cjobs.outcome.unknown';
}

/** The success toast of a release, by outcome. */
export function releaseDoneMessageId(outcome: string): string {
  return OUTCOMES.includes(outcome) ? `cjobs.done.${outcome}` : 'cjobs.done.generic';
}

/** A release reason's label (an unknown historical code falls back to a generic label). */
export function releaseReasonMessageId(reasonCode: string): string {
  return (COURIER_RELEASE_REASONS as readonly string[]).includes(reasonCode) ? `cjobs.reason.${reasonCode}` : 'cjobs.reason.unknown';
}

/** Refusals meaning the list the operator looked at is stale: say so and read it again. */
const STALE_REFUSALS: readonly string[] = [
  'COURIER_JOB_NOT_FOUND',
  'COURIER_JOB_NOT_ASSIGNED',
  'COURIER_JOB_ALREADY_ENDED',
  'COURIER_JOB_CHANGED',
  'COURIER_RELEASE_REASON_NOT_APPLICABLE',
  'IDEMPOTENCY_KEY_REUSED',
];

/** Whether a refused release means the row changed under the operator (the screen re-reads the list). */
export function releaseRefusalRefreshesList(error: Pick<ApiError, 'reasonCode'>): boolean {
  return error.reasonCode !== undefined && STALE_REFUSALS.includes(error.reasonCode);
}

/** Whether the console offers the release action at all (the server re-authorizes at the job's market). */
export function mayReleaseCourierJobs(grants: KnownGrants): boolean {
  return grants === undefined || grantsAllow(grants, { permission: 'courier_job:release', target: 'GEO' });
}

/** A short, readable tail of an opaque id (the list is not the place for a full UUID). */
export function shortId(id: string, length = 8): string {
  return id.length <= length ? id : `…${id.slice(-length)}`;
}

export interface CourierMarketChoice {
  readonly id: string;
  readonly name: string;
}

/**
 * The markets the picker offers when the operator's scope names no city: the registered operating markets (not
 * retired), narrowed to the scoped country when one is selected, to the scoped state's markets when a state is, and to the cities the operator's grants reach when
 * the scope lists any (a city grant is the authority even if the catalog does not list it). Sorted by name.
 */
export function courierJobMarketChoices(scope: ScopeState, markets: readonly OperatingMarketView[]): readonly CourierMarketChoice[] {
  const name = new Map(markets.map((m) => [m.id, m.name]));
  const reachable = new Set(scope.allowed.map((s) => s.city).filter((c): c is string => c !== undefined));
  const ids = new Set<string>([...markets.filter((m) => m.status !== 'RETIRED').map((m) => m.id), ...reachable]);
  const country = scope.current.country?.toUpperCase();
  // A state scope offers only the markets of that state (a state with none offers none - never its whole country).
  const inState = scope.current.region !== undefined && scope.current.city === undefined ? new Set(marketsOfRegion(scope.current.region, markets).map((m) => m.id)) : undefined;
  return [...ids]
    .filter((id) => (reachable.size === 0 || reachable.has(id)) && (country === undefined || countryOfMarket(id) === country) && (inState === undefined || inState.has(id)))
    .map((id) => ({ id, name: name.get(id) ?? id }))
    .sort((a, b) => a.name.localeCompare(b.name));
}

/** The market the list reads: the scoped city; else the operator's pick; else the only choice; else none. */
export function courierJobsMarket(scope: ScopeState, choices: readonly CourierMarketChoice[], picked: string | undefined): string | undefined {
  if (scope.current.city !== undefined) return scope.current.city;
  if (picked !== undefined && choices.some((c) => c.id === picked)) return picked;
  return choices.length === 1 ? choices[0]?.id : undefined;
}

// ---- D856 (owner R2): what releasing a Send already picked up does to the money, in words ------------------------------------------------------------

/** One sentence of the outcome: a message id and its params (money already formatted by the screen's formatter). */
export interface AfterPickupSentence {
  readonly messageId: string;
  readonly params: Readonly<Record<string, string>>;
}

const isZero = (m: AfterPickupMoney): boolean => m.amountMinor === '0';

/**
 * The sentences the release dialog shows for ONE outcome of a Send released after its pickup: what the sender pays, what the courier receives, the return, and what becomes of the
 * cash the courier took at the pickup (kept as payment, handed back with the package) and of whatever the sender still owes. The figures are the server's; nothing is computed here.
 */
export function afterPickupSentences(outcome: SendAfterPickupOutcomeView, money: (m: AfterPickupMoney) => string): readonly AfterPickupSentence[] {
  const out: AfterPickupSentence[] = [];
  out.push(isZero(outcome.charged) ? { messageId: 'cjobs.after.chargedNone', params: {} } : { messageId: 'cjobs.after.charged', params: { amount: money(outcome.charged) } });
  if (outcome.cause === 'PLATFORM_FAULT') {
    // D858: the platform failed - the sender is never charged and Vello itself pays the courier (what the city's rule says, possibly nothing).
    const paid = outcome.platformPaidCourier;
    out.push(paid === undefined || isZero(paid) ? { messageId: 'cjobs.after.platformPaysNone', params: {} } : { messageId: 'cjobs.after.platformPays', params: { amount: money(paid) } });
  } else {
    out.push(isZero(outcome.courierEarned) ? { messageId: 'cjobs.after.courierNone', params: {} } : { messageId: 'cjobs.after.courier', params: { amount: money(outcome.courierEarned) } });
  }
  if (outcome.returnToSender) {
    out.push(outcome.returnCharged && !isZero(outcome.returnCharge) ? { messageId: 'cjobs.after.returnCharged', params: { amount: money(outcome.returnCharge) } } : { messageId: 'cjobs.after.returnFree', params: {} });
  }
  if (isZero(outcome.cash.held)) {
    out.push({ messageId: 'cjobs.after.cashNone', params: {} });
  } else {
    if (!isZero(outcome.cash.applied)) out.push({ messageId: 'cjobs.after.cashApplied', params: { applied: money(outcome.cash.applied), held: money(outcome.cash.held) } });
    if (!isZero(outcome.cash.returnedToSender)) out.push({ messageId: 'cjobs.after.cashReturned', params: { amount: money(outcome.cash.returnedToSender) } });
  }
  if (!isZero(outcome.senderDebt)) out.push({ messageId: 'cjobs.after.debt', params: { amount: money(outcome.senderDebt) } });
  return out;
}

/** The outcome the dialog shows for the reason picked (undefined until a reason is picked, or when the job carries no preview: a food job, a desk Send, an unreadable rule). */
export function afterPickupOutcomeFor(preview: SendAfterPickupPreview | undefined, reasonCode: string, attribution?: string): SendAfterPickupOutcomeView | undefined {
  if (preview === undefined || reasonCode.trim() === '') return undefined;
  // D858: a PIN-locked release has no cause until the operator says whose error it was.
  const cause = afterPickupCauseOfReason(reasonCode, attribution);
  return cause === undefined ? undefined : preview.byCause[cause];
}
